import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  ChooseWebsitePageActions,
  LoginPageActions,
  LoginSettingsActions,
} from './login-settings.actions';
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  first,
  map,
  of,
  tap,
} from 'rxjs';
import { Router } from '@angular/router';
import { LoginSettingsService } from '../../login-settings.service';
import {
  ApplicationActions,
  ApplicationFacade,
  AppUrlService,
} from '@lyceo/shared/data-access';
import { Capacitor } from '@capacitor/core';
import { LoginSettingsFacade } from './login-settings.facade';
import { isDefined } from '@lyceo/shared/util';
import { AuthActions, AuthService } from '@lyceo/shared/auth/data-access';
import {
  ENVIRONMENT,
  EnvironmentModel,
} from '@lyceo/homi/shared/util-environment';
import { getWebsiteFromLocation } from '@lyceo/homi/login/util';
import { TranslocoService } from '@jsverse/transloco';

@Injectable()
export class LoginSettingsEffects {
  private readonly actions$ = inject(Actions);
  private readonly router: Router = inject(Router);
  private readonly loginSettingsService: LoginSettingsService =
    inject(LoginSettingsService);
  private readonly applicationFacade: ApplicationFacade =
    inject(ApplicationFacade);
  private readonly loginSettingsFacade: LoginSettingsFacade =
    inject(LoginSettingsFacade);
  private readonly authService: AuthService = inject(AuthService);
  private readonly environment: EnvironmentModel =
    inject<EnvironmentModel>(ENVIRONMENT);
  private readonly translocoService: TranslocoService =
    inject(TranslocoService);
  private readonly appUrlService: AppUrlService = inject(AppUrlService);

  public getSettingsWhenAppStartsDesktop$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApplicationActions.init),
      filter(() => Capacitor.getPlatform() === 'web'),
      map(() =>
        LoginSettingsActions.getLoginSettings({
          website: getWebsiteFromLocation(
            window.location,
            this.environment.production,
          ),
        }),
      ),
    );
  });

  public getSettingsWhenAppStartsMobile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApplicationActions.init),
      filter(() => Capacitor.getPlatform() !== 'web'),
      map(() => this.loginSettingsFacade.website()),
      filter(isDefined),
      map((website) => LoginSettingsActions.getLoginSettings({ website })),
    );
  });

  public getSettingsWhenWebsiteIsChosen$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        LoginSettingsActions.getLoginSettings,
        ChooseWebsitePageActions.websiteChosen,
      ),
      exhaustMap((action) =>
        this.loginSettingsService.getLoginSettings(action.website).pipe(
          map((settings) =>
            LoginSettingsActions.getLoginSettingsSuccess({
              settings,
            }),
          ),
          catchError((error) =>
            of(
              LoginSettingsActions.getLoginSettingsFailure({
                error,
              }),
            ),
          ),
        ),
      ),
    );
  });

  public navigateToHome$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ChooseWebsitePageActions.websiteChosen),
        tap(() => {
          this.router.navigate(['/home']);
        }),
      );
    },
    { dispatch: false },
  );

  public showLoginSettingsError$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(LoginSettingsActions.getLoginSettingsFailure),
        concatMap(() =>
          this.translocoService
            .selectTranslate('toasts.login_settings_load_failure')
            .pipe(first()),
        ),
        map((message) => {
          this.applicationFacade.showToaster({
            type: 'error',
            message: message,
          });
        }),
      );
    },
    { dispatch: false },
  );

  public initAuth$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LoginSettingsActions.getLoginSettingsSuccess),
      map((action) => {
        this.authService.configure({
          ...this.environment.authConfig,
          redirectUri: this.appUrlService.buildUrl('home'),
          postLogoutRedirectUri: this.appUrlService.buildUrl('login'),
          issuer: `${this.environment.issuerUrl}/realms/${action.settings.realm}`,
          clientId: action.settings.websiteId,
        });
        return AuthActions.loadDiscoveryDocument();
      }),
    );
  });

  public websiteLogout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ChooseWebsitePageActions.websiteLogout),
      map(() => AuthActions.userLogout()),
    );
  });

  public clearWebsite$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(LoginPageActions.clearWebsite),
        tap(() => this.router.navigate(['/website'])),
      );
    },
    { dispatch: false },
  );
}
